import { Card, Grid, Link, MenuItem, Stack, Typography } from "@mui/material";
import React from "react";
import { FileStatus } from "../../../Core/Api/Types/file-status";
import { ObjectType } from "../../../Core/Api/Types/object-type";
import AssetTypeChip from "../../Misc/Chips/AssetChip/asset-type-chip";
import FileStatusChip from "../../Misc/Chips/FileStatusChip/file-status-chip";
import MoreMenu from "../../Misc/MoreMenu";
import { ModalContext } from "../../Modals";
import ModalRequestDownloadLink, {
  ModalRequestDownloadLinkElementProps,
} from "../../Modals/ModalRequestDownloadLink/modal-request-download-link";
import { useTranslation } from "react-i18next";

export interface MediaCardProps {
  fileId: number;
  isDownloable: boolean;
  downloadLink?: string;
  title: string;
  type: ObjectType;
  status: FileStatus;
  duration: string;
  storageLocation: string;
  category: string;
  accessibility: string;
  size: string;
  date: string;
  hideRequestDownloadLink?;
}

const MediaCard: React.FC<MediaCardProps> = ({
  fileId,
  isDownloable,
  downloadLink,
  title,
  type,
  status,
  duration,
  storageLocation,
  category,
  accessibility,
  size,
  date,
  hideRequestDownloadLink = undefined,
}) => {
  const { t } = useTranslation();
  const modalContext = React.useContext(ModalContext);
  const { openModal } = { ...modalContext };

  const handleClickDownloadLink = () => {
    if (!openModal) return;

    openModal<ModalRequestDownloadLinkElementProps>(ModalRequestDownloadLink, {
      fileIds: [fileId],
    });
  };

  return (
    <Card sx={{ p: 2 }} variant="outlined">
      <Stack mb={2} direction="row" alignItems="center" justifyContent="space-between" spacing={1.5}>
        <Stack direction="row" alignItems="center">
          {isDownloable && downloadLink ? (
            <Link
              href={downloadLink}
              underline="hover"
              color="primary.text"
              mr={1.5}
              variant="body1"
              sx={{ fontWeight: 500 }}
              target="_blank"
            >
              {title}
            </Link>
          ) : (
            <Typography mr={1.5} variant="body1" sx={{ fontWeight: 500 }}>
              {title}
            </Typography>
          )}
        </Stack>
        <Stack spacing={1} alignItems="center">
          <AssetTypeChip type={type} size="small" />
          <FileStatusChip status={status} />
          {status === FileStatus.Online && hideRequestDownloadLink === undefined && (
            <MoreMenu sx={{ ml: 1.5 }}>
              <MenuItem onClick={handleClickDownloadLink}>{t("modal:title.createDownloadLink")}</MenuItem>
            </MoreMenu>
          )}
        </Stack>
      </Stack>
      <Grid container spacing={2}>
        <Grid item md={4}>
          <Typography variant="body2" fontWeight={500} color="text.tier">
            {t("common:duration")}
          </Typography>
          <Typography>{duration}</Typography>
        </Grid>
        <Grid item md={4}>
          <Typography variant="body2" fontWeight={500} color="text.tier">
            {t("common:storageLocation")}
          </Typography>
          <Typography>{storageLocation}</Typography>
        </Grid>
        <Grid item md={4}>
          <Typography variant="body2" fontWeight={500} color="text.tier">
            {t("common:category")}
          </Typography>
          <Typography>{category}</Typography>
        </Grid>
        <Grid item md={4}>
          <Typography variant="body2" fontWeight={500} color="text.tier">
            {t("common:accessibility")}
          </Typography>
          <Typography>{accessibility}</Typography>
        </Grid>
        <Grid item md={4}>
          <Typography variant="body2" fontWeight={500} color="text.tier">
            {t("common:size")}
          </Typography>
          <Typography>{size}</Typography>
        </Grid>
        <Grid item md={4}>
          <Typography variant="body2" fontWeight={500} color="text.tier">
            {t("common:date")}
          </Typography>
          <Typography>{date}</Typography>
        </Grid>
      </Grid>
    </Card>
  );
};

export default MediaCard;
